import {
    ComponentType, useCallback, useMemo, useState,
} from 'react';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { hideAppSpinner, showAppNotification, showAppSpinner } from '@rainbow-modules/app';
import { ChatMessage } from 'types';
import PaperIcon from 'components/icons/paper';
import ChatIcon from 'components/icons/chat2';
import SettingIcon from 'components/icons/setting';
import useResponderInstructionsValidation from 'data/hooks/useResponderInstructionsValidation';
import InstructionTestingLayout from 'components/InstructionTestingLayout';
import toTestConfig from '../../helpers/toTestConfig';
import StepCompileSystemMessage from '../views/systemMessage';
import StepOutputView from '../views/OutputView';
import StepInputView from '../views/input';
import StepConversation from '../views/conversation';
import {
    ResponderInstructionTestComponentProps,
    ResponderInstructionTestingContext,
    ResponderInstructionTestStepComponentProps,
} from '../types';
import generateId from '../helpers/generateId';
import isConversationalResponder from '../helpers/isConversationalResponder';

// eslint-disable-next-line max-len
const stepComponentMap: Record<string, ComponentType<ResponderInstructionTestStepComponentProps>> = {
    input: StepInputView,
    'system-message': StepCompileSystemMessage,
    conversation: StepConversation,
    output: StepOutputView,
};

interface Params extends ResponderInstructionTestComponentProps {}

const MainInstructionTesting = ({
    template,
    agentId,
    responderId,
    responderConfig,
}: Params) => {
    const [currentStep, setCurrentStep] = useState('input');
    // eslint-disable-next-line max-len
    const [contextValue, setContextValue] = useState<ResponderInstructionTestingContext>({ responderConfig });

    const { mutateAsync: compileTemplate } = useResponderInstructionsValidation(
        agentId as string,
        responderId,
    );

    const {
        mutateAsync: invokeResponder,
    } = useHttpMutation<Record<string, unknown>, Record<string, unknown>>({
        pathname: `/agents/${agentId}/llms/${responderId}/test`,
        method: 'POST',
        server: process.env.REACT_APP_BASE_URL,
    });

    const {
        mutateAsync: replayConversation,
    } = useHttpMutation<Record<string, unknown>, Record<string, unknown>>({
        pathname: `/agents/${agentId}/llms/${responderId}/test/replay-conversation`,
        method: 'POST',
        server: process.env.REACT_APP_BASE_URL,
    });

    const handleStepUpdate = useCallback(
        (stepName: string) => async (values: Record<string, unknown>) => {
            const newContext: Record<string, unknown> = { ...contextValue };

            if (stepName === 'input') {
                const { context, formValues } = values;
                const customerId = (formValues as any)?.customer?.id || '';
                newContext.inputValues = formValues;
                newContext.templateContext = context;
                newContext.compiledTemplate = '';
                showAppSpinner();
                try {
                    const { result } = await compileTemplate({
                        pathname: `/agents/${agentId}/llms/${responderId}/system-message/validate`,
                        body: {
                            template,
                            context: context as any,
                            customerId,
                        },
                    });
                    newContext.compiledTemplate = result;
                    setCurrentStep('system-message');
                } catch (error: any) {
                    const errorMessage = (
                        error?.errors
                            ? error?.errors?.map((err: any) => err.message).join(', ')
                            : error.message || 'Something went wrong. Please try again.'
                    );
                    showAppNotification({
                        title: 'Error',
                        description: errorMessage,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            }

            if (stepName === 'conversation') {
                newContext.chatMessages = values.messages as ChatMessage[];
                const { submit = false, replay = false } = values as Record<string, boolean>;
                if (submit || replay) {
                    delete newContext.output;
                    newContext.replayMode = replay;
                    const invokeFn = replay ? replayConversation : invokeResponder;
                    showAppSpinner();
                    try {
                        const response = await invokeFn({
                            body: {
                                input: newContext.templateContext,
                                messages: newContext.chatMessages,
                                template,
                                config: toTestConfig(responderConfig),
                            },
                        });
                        newContext.output = response;
                        if (isConversationalResponder(responderConfig)) {
                            const chatMessages = newContext.chatMessages as ChatMessage[] || [];
                            newContext.chatMessages = [
                                ...chatMessages,
                                {
                                    id: `${generateId()}-${chatMessages.length}`,
                                    content: response.content,
                                    role: 'assistant',
                                },
                            ];
                        } else {
                            setCurrentStep('output');
                        }
                    } catch (error) {
                        const message = (error as any)?.message || 'Something went wrong. Please try again.';
                        showAppNotification({
                            title: 'Error',
                            description: message,
                            icon: 'error',
                            timeout: 5000,
                        });
                    }
                    hideAppSpinner();
                }
            }

            if (stepName === 'output') {
                newContext.output = values.output as Record<string, unknown>;
            }
            setContextValue(newContext);
        },
        [
            agentId,
            compileTemplate,
            contextValue,
            invokeResponder,
            replayConversation,
            responderId,
            responderConfig,
            template,
        ],
    );

    const diagnostics = contextValue.output?.diagnostics as {
        model: string; reasoningEffort?: string; warnings: string[];
    } | undefined;
    const StepComponent = useMemo(() => stepComponentMap[currentStep], [currentStep]);

    return (
        <InstructionTestingLayout
            activeStep={currentStep}
            steps={[
                {
                    name: 'input',
                    label: 'Input',
                    icon: <SettingIcon />,
                },
                {
                    name: 'system-message',
                    label: 'System Message',
                    icon: <PaperIcon />,
                    disabled: !contextValue.compiledTemplate,
                },
                {
                    name: 'conversation',
                    label: 'Conversation',
                    icon: <ChatIcon />,
                    disabled: !contextValue.compiledTemplate,
                },
                {
                    name: 'output',
                    label: 'Output',
                    icon: <ChatIcon />,
                    disabled: !contextValue.output,
                },
            ]}
            onStepChange={setCurrentStep}
            contextValue={contextValue}
        >
            {Boolean(diagnostics) && (
                <p role="status" className="rainbow-p-horizontal_medium">
                    Tested configuration:
                    {' '}
                    {diagnostics?.model}
                    {' · Reasoning: '}
                    {diagnostics?.reasoningEffort || 'not applicable'}
                    .
                    {' Configuration changes are not saved by testing.'}
                    {(diagnostics?.warnings || []).map((warning: string) => (
                        <span key={warning}>
                            {' '}
                            {warning}
                            .
                        </span>
                    ))}
                </p>
            )}
            <StepComponent
                agentId={agentId}
                responderId={responderId}
                systemMessageTemplate={template}
                onChange={handleStepUpdate(currentStep)}
            />
        </InstructionTestingLayout>
    );
};

export default MainInstructionTesting;
